import { useSelector } from "react-redux";
// StockSelectModal - ピースの cover / background をユーザーの「持ち物（ストック）」から選ぶ Modal。
// 詳しく(BuilderPieceDetail)の「カバーをかえる」「はいけいをかえる」から開く。
// データは getUserAssets（UserStock と同じ React Query キャッシュ）。ALBUM/DRAWING/AI のタブで絞り込み、
// 画像をタップしたら onSelect(url) を返す（適用・保存は呼び出し側）。
// ALBUM では端末から画像追加、AI ではAI生成が可能（保存後 onAssetSaved でグリッドに即反映）。
import { useState, useMemo, useRef } from "react";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { Asset } from "app/types";
import { RootState } from "app/store";
import { usePickImageAsset } from "app/hook";
import I18n from "common/i18n";
import { usePlaySound } from "common/utils";
import { AIGenerateOnlyModal } from "features/generation";
import { getUserAssets, AssetsResponse } from "features/user/api";
import { InScreenModal, BuilderModalPanel, BuilderModalAccent } from "features/builder/elements";

enum StockTab {
  ALBUM = "album",
  DRAWING = "drawing",
  AI = "ai",
}

const TAB_ITEMS: { id: StockTab; labelKey: string; icon: string }[] = [
  {
    id: StockTab.ALBUM,
    labelKey: "MSG_CREATOR_MENU_ASSET_CATEGORY_ALBUM_TITLE",
    icon: "photo_library",
  },
  {
    id: StockTab.DRAWING,
    labelKey: "MSG_CREATOR_MENU_ASSET_CATEGORY_DRAWING_TITLE",
    icon: "edit_square",
  },
  { id: StockTab.AI, labelKey: "MSG_USER_STOCK_AI_TAB", icon: "auto_awesome" },
];

const isAIAsset = (asset: Asset) => asset.tags?.startsWith("ai_generated:");

export const StockSelectModal = ({
  open,
  target,
  currentUrl,
  onSelect,
  onClose,
}: {
  open: boolean;
  target: "cover" | "background";
  currentUrl: string | null;
  onSelect: (url: string) => void;
  onClose: () => void;
}) => {
  const play = usePlaySound();
  const uid = useSelector((state: RootState) => state.user.active?.uid);
  const pickImageAsset = usePickImageAsset();
  const inputRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<StockTab>(StockTab.ALBUM);
  const [showAIModal, setShowAIModal] = useState(false);

  const { data } = useQuery<AssetsResponse>({
    queryKey: ["creator/getUserAssets", uid],
    queryFn: () => getUserAssets({ uid: uid as string }),
    staleTime: Infinity,
    enabled: !!uid && open,
    placeholderData: { assets: [], totalSize: 0 },
  });
  const items = useMemo(() => {
    const list = Array.isArray(data?.assets) ? data.assets : [];
    return list
      .filter((a) => {
        if (tab === StockTab.AI) return isAIAsset(a);
        if (tab === StockTab.DRAWING) return /\.svg/.test(a.url) && !isAIAsset(a);
        return !/\.svg/.test(a.url) && !isAIAsset(a);
      })
      .sort((a, b) => (dayjs(a.created_at) < dayjs(b.created_at) ? 1 : -1));
  }, [data, tab]);

  const handleTab = (id: StockTab) => () => {
    if (id === tab) return;
    play();
    setTab(id);
  };

  const handleSelect = (asset: Asset) => () => {
    play();
    onSelect(asset.url);
  };

  const handlePickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uid) return;
    const clear = () => {
      if (inputRef.current) inputRef.current.value = "";
    };
    await pickImageAsset(file, uid, clear, clear);
  };

  // DRAWING タブのみ「作成タイル」が無いので、空のときは空メッセージを出す
  const showEmpty = tab === StockTab.DRAWING && items.length === 0;

  return (
    <>
      <InScreenModal open={open} onBackdropClose={onClose}>
        <BuilderModalPanel
          accent={BuilderModalAccent.BLUE}
          icon="image"
          title={I18n.t(
            target === "cover"
              ? "MSG_BUILDER_DETAIL_CHANGE_COVER"
              : "MSG_BUILDER_DETAIL_CHANGE_BACKGROUND",
          )}
          width={760}
          onClose={onClose}
        >
          <div className="flex-col-el w-full gap-3">
            {/* タブ */}
            <div className="flex-row-el items-center gap-2">
              {TAB_ITEMS.map(({ id, labelKey, icon }) => {
                const isActive = id === tab;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={handleTab(id)}
                    className={`type-inherit-font flex-row-el flex-center gap-1 box-border h-12 px-4 rounded-full border-none cursor-pointer ${
                      isActive ? "bg-blue-100" : "bg-beige-60"
                    }`}
                  >
                    <span
                      className={`material-symbols-outlined type-md ${
                        isActive ? "text-white" : "text-textcolor-black"
                      }`}
                    >
                      {icon}
                    </span>
                    <span
                      className={`type-text type-sm whitespace-nowrap ${
                        isActive ? "text-white" : "text-textcolor-black"
                      }`}
                    >
                      {I18n.t(labelKey)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* グリッド（固定高さ・スクロール） */}
            <div className="flex-col-el items-stretch w-full h-[400px] bg-beige-60 rounded-xl box-border p-3">
              {showEmpty ? (
                <div className="flex-col-el flex-center w-full h-full">
                  <span className="type-text type-body text-textcolor-black/40">
                    {I18n.t("MSG_ASSET_DRAWING_EMPTY_MESSAGE")}
                  </span>
                </div>
              ) : (
                <div className="flex-row-el flex-wrap items-start content-start w-full h-full gap-3 overflow-y-auto invisible-scrollbar">
                  {/* ALBUM: 端末から画像追加タイル */}
                  {tab === StockTab.ALBUM && (
                    <div className="flex-col-el flex-center relative box-border w-[140px] h-[140px] rounded-xl overflow-hidden bg-green-100">
                      <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePickImage}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      />
                      <span className="material-symbols-outlined type-hero text-white">
                        add_photo_alternate
                      </span>
                      <span className="type-text type-sm text-white">
                        {I18n.t("MSG_ASSET_ALBUM_ADD_TITLE")}
                      </span>
                    </div>
                  )}

                  {/* AI: AI生成タイル */}
                  {tab === StockTab.AI && (
                    <button
                      type="button"
                      onClick={() => {
                        play();
                        setShowAIModal(true);
                      }}
                      className="type-inherit-font flex-col-el flex-center box-border w-[140px] h-[140px] rounded-xl overflow-hidden border-none cursor-pointer bg-blue-100"
                    >
                      <span className="material-symbols-outlined type-hero text-white">
                        auto_awesome
                      </span>
                      <span className="type-text type-sm text-white">
                        {I18n.t("MSG_AI_TAB_GENERATE")}
                      </span>
                    </button>
                  )}

                  {items.map((asset, index) => {
                    const selected = asset.url === currentUrl;
                    return (
                      <button
                        key={asset.id}
                        type="button"
                        onClick={handleSelect(asset)}
                        className={`type-inherit-font flex-col-el flex-center box-border w-[140px] h-[140px] rounded-xl overflow-hidden cursor-pointer bg-white ${
                          selected ? "border-solid border-4 border-green-100" : "border-none"
                        }`}
                      >
                        <img
                          alt={asset.name}
                          loading={index > 12 ? "lazy" : "eager"}
                          src={asset.url}
                          className="w-full h-full object-contain"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </BuilderModalPanel>
      </InScreenModal>

      <AIGenerateOnlyModal
        open={showAIModal && !!uid}
        onClose={() => setShowAIModal(false)}
        uid={uid ?? ""}
      />
    </>
  );
};
